import { useEffect, useMemo, useRef } from 'react';
import isEqual from 'lodash/isEqual';

import { MedicineSimple } from '@dreryk/drerykweb-medicine-api';

import {
   ExtendedDosage,
   getExtendedDosageAdministrationDaysCount,
   getExtendedDosageMedicineDemand,
   getExtendedDosagePackagesCount,
   getExtendedDosageTreatmentDaysCount,
   getTreatmentDaysCountMaxValue,
   hasCompositePackage,
   hasKnownPackageCapacity,
} from '~libs.shared.prescription';

import { DraftPrescriptionsFormController } from '../DraftPrescriptionsForm.types';

import { createDraftStandardPrescriptionsFieldNameFactory } from './DraftStandardPrescriptionField.utils';

type UseExtendedDosageStateOptions = {
   medicine: MedicineSimple;
   extendedDosage: ExtendedDosage | null;
   issuedForYear: boolean;
};

export type ExtendedDosageState = {
   active: boolean;
   packagesCount: number | undefined;
   treatmentDaysCount: number | undefined;
   administrationDaysCount: number | undefined;
   medicineDemand: number | undefined;
   packagesCountEditable: boolean;
   maxValue: number;
   exceeded: boolean;
   exceededMaxValue: number;
   changeToPrescriptionForYearAvailable: boolean;
};

export const useExtendedDosageState = (options: UseExtendedDosageStateOptions): ExtendedDosageState => {
   const { medicine, extendedDosage, issuedForYear } = options;

   return useMemo(() => {
      const active = Boolean(extendedDosage?.stages.length);
      const maxValue = getTreatmentDaysCountMaxValue({ issuedForYear: issuedForYear });

      if (!active || !extendedDosage) {
         return {
            active: false,
            packagesCount: undefined,
            treatmentDaysCount: undefined,
            administrationDaysCount: undefined,
            medicineDemand: undefined,
            packagesCountEditable: false,
            maxValue: maxValue,
            exceeded: false,
            exceededMaxValue: maxValue,
            changeToPrescriptionForYearAvailable: !issuedForYear,
         };
      }

      const packagesCount = getExtendedDosagePackagesCount(extendedDosage, medicine);
      const treatmentDaysCount = getExtendedDosageTreatmentDaysCount(extendedDosage);
      const yearMaxValue = getTreatmentDaysCountMaxValue({ issuedForYear: true });
      const exceedingYearLimit = treatmentDaysCount !== undefined && treatmentDaysCount > yearMaxValue;

      return {
         active: true,
         packagesCount: packagesCount,
         treatmentDaysCount: treatmentDaysCount,
         administrationDaysCount: getExtendedDosageAdministrationDaysCount(extendedDosage),
         medicineDemand: getExtendedDosageMedicineDemand(extendedDosage),
         packagesCountEditable: !hasKnownPackageCapacity(medicine) || hasCompositePackage(medicine),
         maxValue: maxValue,
         exceeded: treatmentDaysCount !== undefined && treatmentDaysCount > maxValue,
         exceededMaxValue: exceedingYearLimit ? yearMaxValue : maxValue,
         changeToPrescriptionForYearAvailable: !issuedForYear && !exceedingYearLimit,
      };
   }, [medicine, extendedDosage, issuedForYear]);
};

type UseWatchedExtendedDosageOptions = {
   watched: ExtendedDosage | null | undefined;
   fallback: ExtendedDosage | null;
};

export const useWatchedExtendedDosage = (options: UseWatchedExtendedDosageOptions): ExtendedDosage | null => {
   const value = options.watched === undefined ? options.fallback : options.watched;
   const stableValue = useRef<ExtendedDosage | null>(value);

   if (!isEqual(stableValue.current, value)) {
      stableValue.current = value;
   }

   return stableValue.current;
};

type UseExtendedDosageComputedValuesSyncOptions = Pick<DraftPrescriptionsFormController, 'setValue'> & {
   index: number;
   extendedDosage: ExtendedDosageState;
   initialQuantity: number | null;
};

export const useExtendedDosageComputedValuesSync = (options: UseExtendedDosageComputedValuesSyncOptions): void => {
   const createFieldName = createDraftStandardPrescriptionsFieldNameFactory(options.index);
   const treatmentDaysCountFieldName = createFieldName('treatmentDaysCount');
   const quantityFieldName = createFieldName('quantity');

   const { setValue, extendedDosage, initialQuantity } = options;
   const previouslyActiveRef = useRef(extendedDosage.active);

   useEffect(() => {
      if (!extendedDosage.active) {
         if (previouslyActiveRef.current) {
            previouslyActiveRef.current = false;
            setValue(quantityFieldName, initialQuantity, { shouldValidate: true });
         }

         return;
      }

      const becameActive = !previouslyActiveRef.current;
      previouslyActiveRef.current = true;
      setValue(treatmentDaysCountFieldName, extendedDosage.treatmentDaysCount ?? null, { shouldValidate: true });

      if (!extendedDosage.packagesCountEditable) {
         setValue(quantityFieldName, extendedDosage.packagesCount ?? null, { shouldValidate: true });

         return;
      }

      if (becameActive) {
         setValue(quantityFieldName, null, { shouldValidate: true });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [
      extendedDosage.active,
      extendedDosage.treatmentDaysCount,
      extendedDosage.packagesCount,
      extendedDosage.packagesCountEditable,
      initialQuantity,
   ]);
};
